/**
 * Variant selection and the quantity stepper.
 *
 * The picker is a set of real links to real URLs. Verified in Phase 8B against
 * the live store: a product page honours `?option_values=<id>,<id>`, ignores
 * `?option1=`, and keeps only the last of repeated `option_values` params — so a
 * radio group cannot select a variant server-side, and a link can. With no
 * JavaScript the link navigates and the server renders the right variant. This
 * file only upgrades that navigation into a section swap.
 *
 * The click handler is delegated from `document`, not bound inside
 * `<variant-picker>`, because the swap replaces the whole section — including
 * the picker that would have been handling the event.
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  let inFlight = null;

  /**
   * Replace the entire `shopify-section` wrapper. Rendering into an inner
   * element nests a second wrapper and duplicates its DOM id.
   */
  async function selectVariant(url, sectionId, options) {
    const push = !options || options.push !== false;

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    const section = document.getElementById('shopify-section-' + sectionId);
    if (!section) return;
    section.setAttribute('aria-busy', 'true');

    // Restore focus onto the replacement for whatever had it, identified by the
    // combination it points at rather than by a node that is about to be gone.
    const active = document.activeElement;
    const focusedCombination =
      active && active.dataset ? active.dataset.optionValues : null;

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', sectionId);

      const response = await fetch(target.toString(), { signal: inFlight.signal });
      if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
      const html = await response.text();

      section.outerHTML = html;

      if (push) {
        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        window.history.pushState({ variant: true }, '', clean.toString());
      }

      if (focusedCombination) {
        const restored = document.querySelector(
          '[data-option-values="' + focusedCombination + '"]'
        );
        if (restored) restored.focus({ preventScroll: true });
      }

      const picker = document.querySelector('variant-picker[data-section-id="' + sectionId + '"]');
      if (picker && picker.dataset.announcement) {
        window.Theme.announce(picker.dataset.announcement);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // The link was always going to work on its own.
      window.location.href = url;
    } finally {
      inFlight = null;
      const current = document.getElementById('shopify-section-' + sectionId);
      if (current) current.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-variant-option-link]');
    if (!link || link.tagName !== 'A') return;

    // Let the browser handle anything that is not a plain left click.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const picker = link.closest('variant-picker');
    if (!picker) return;

    event.preventDefault();
    selectVariant(link.href, picker.dataset.sectionId);
  });

  window.addEventListener('popstate', () => {
    const picker = document.querySelector('variant-picker[data-section-id]');
    if (!picker) return;
    selectVariant(window.location.href, picker.dataset.sectionId, { push: false });
  });

  /**
   * Delivery estimate.
   *
   * The courier prices ~7,200 towns into five bands. That table is far too big
   * to inline, so it lives in `assets/delivery-rates.json` and is fetched once,
   * lazily, the first time the customer actually interacts — never on page
   * load, and never at all for the majority who don't use it. The result is
   * cached on the constructor so a second product page reuses it.
   *
   * Prices never appear in this file. Liquid renders the five amounts through
   * `money` into `data-tiers`, and the JSON carries only a tier index, so there
   * is exactly one place the currency is formatted.
   */
  class DeliveryEstimate extends window.Theme.ThemeElement {
    init() {
      this.input = this.querySelector('[data-delivery-input]');
      this.picker = this.querySelector('[data-delivery-picker]');
      this.list = this.querySelector('[data-delivery-list]');
      this.rows = this.querySelector('[data-delivery-rows]');
      this.summary = this.querySelector('[data-delivery-summary]');
      this.miss = this.querySelector('[data-delivery-miss]');
      this.status = this.querySelector('[data-delivery-status]');
      if (!this.input || !this.rows || !this.list) return;

      // NOT `this.title`: every custom element inherits HTMLElement.title, so
      // assigning an element to it coerces to "[object HTMLSpanElement]" and the
      // next property access throws — silently, before any listener is bound.
      this.heading = this.querySelector('[data-delivery-title]');
      this.baseHeading = this.heading ? this.heading.textContent.trim() : '';

      this.tiers = (this.dataset.tiers || '').split('|').filter(Boolean);
      // One delivery time per band, or none. The server only emits the list
      // when its count matches the rates; the fallback is the single ETA the
      // block rendered.
      const etas = (this.dataset.etas || '').split('|').filter(Boolean);
      this.etas = etas.length === this.tiers.length ? etas : [];
      const etaEl = this.querySelector('[data-delivery-eta]');
      this.baseEta = etaEl ? etaEl.textContent : '';
      this.active = -1;

      this.on(this.input, 'focus', () => this.load());
      this.on(this.input, 'input', () => this.load().then(() => this.search()));
      this.on(this.input, 'keydown', (event) => this.onKeydown(event));
      this.on(this.list, 'mousedown', (event) => {
        // mousedown, not click: blur fires first and would close the list.
        const option = event.target.closest('[data-town]');
        if (!option) return;
        event.preventDefault();
        this.choose(option.dataset.town);
      });
      this.on(this.input, 'blur', () => this.close());

      // Revealed only now, so a customer without JavaScript never sees a box
      // that cannot answer. The island-wide summary is their answer instead.
      this.picker.hidden = false;

      const saved = this.recall();
      if (saved) this.load().then(() => this.choose(saved, true));
    }

    /** Fetch the rate table once per page. Shared across every instance. */
    load() {
      if (DeliveryEstimate.rates) return Promise.resolve(DeliveryEstimate.rates);
      if (this.pending) return this.pending;

      this.pending = fetch(this.dataset.ratesUrl)
        .then((response) => {
          if (!response.ok) throw new Error('Rates ' + response.status);
          return response.json();
        })
        .then((data) => {
          // A tier count mismatch means the setting and the built table have
          // drifted apart. Showing a confidently wrong delivery price is worse
          // than showing none, so the picker goes away instead.
          if (!data.tiers || data.tiers.length !== this.tiers.length) {
            throw new Error('Tier count mismatch');
          }
          // Keyed on the normalised name, valued with the courier's own
          // spelling plus its tiers — so lookup is forgiving and display is not
          // a lower-cased approximation.
          const places = new Map();
          data.byTier.forEach((joined, tier) => {
            if (!joined) return;
            joined.split('|').forEach((name) => {
              places.set(this.normalise(name), { name: name, tiers: [tier] });
            });
          });
          Object.keys(data.ambiguous || {}).forEach((key) => {
            const entry = places.get(key);
            if (entry) entry.tiers = data.ambiguous[key];
          });
          DeliveryEstimate.rates = places;
          return places;
        })
        .catch(() => {
          this.picker.hidden = true;
          this.pending = null;
          return null;
        });

      return this.pending;
    }

    /** Same normalisation as tools/build-delivery-rates.py. */
    normalise(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .replace(/([a-z])(\d)/g, '$1 $2')
        .replace(/\b0+(\d)/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
    }

    search() {
      const rates = DeliveryEstimate.rates;
      const term = this.normalise(this.input.value);
      if (!rates || term.length < 2) return this.close();

      // Prefix matches first — someone typing "nug" means Nugegoda, not
      // "Katunugoda". Substring matches fill the rest of the list.
      const starts = [];
      const contains = [];
      for (const key of rates.keys()) {
        if (key.startsWith(term)) {
          if (starts.length < 8) starts.push(key);
        } else if (starts.length + contains.length < 16 && key.includes(term)) {
          contains.push(key);
        }
        if (starts.length >= 8) break;
      }
      this.render(starts.concat(contains).slice(0, 8));
    }

    render(matches) {
      this.list.textContent = '';
      this.active = -1;
      if (!matches.length) {
        this.close();
        this.announce(window.Theme.strings.deliveryNoResults);
        return;
      }
      matches.forEach((key, index) => {
        const entry = DeliveryEstimate.rates.get(key);
        const item = document.createElement('li');
        item.className = 'delivery__option';
        item.id = this.list.id + '-' + index;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.dataset.town = entry.name;

        const label = document.createElement('span');
        label.className = 'delivery__option-name';
        label.textContent = entry.name;

        const fee = document.createElement('span');
        fee.className = 'delivery__option-fee';
        fee.textContent = this.feeFor(key);

        item.append(label, fee);
        this.list.append(item);
      });
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    /** One tier renders as its price; several render as a range. */
    feeFor(key) {
      const entry = DeliveryEstimate.rates.get(key);
      if (!entry) return '';
      const tiers = entry.tiers;
      if (tiers.length === 1) return this.tiers[tiers[0]];
      const low = this.tiers[Math.min.apply(null, tiers)];
      const high = this.tiers[Math.max.apply(null, tiers)];
      return (window.Theme.strings.deliveryFeeRange || '{min}-{max}')
        .replace('{min}', low)
        .replace('{max}', high);
    }

    /** A town in one band, or in several bands with the same time, gets that
        band's time. Anything else keeps the block's single ETA. */
    etaFor(key) {
      const entry = DeliveryEstimate.rates.get(key);
      if (!entry || !this.etas.length) return this.baseEta;
      const times = entry.tiers.map((tier) => this.etas[tier]);
      return times.every((time) => time === times[0]) ? times[0] : this.baseEta;
    }

    onKeydown(event) {
      const options = Array.from(this.list.children);
      if (event.key === 'Escape') return this.close();
      if (event.key === 'Enter' && this.active > -1) {
        event.preventDefault();
        return this.choose(options[this.active].dataset.town);
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!options.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.active = (this.active + step + options.length) % options.length;
      options.forEach((option, index) => {
        const selected = index === this.active;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    choose(name, restoring) {
      const rates = DeliveryEstimate.rates;
      const key = this.normalise(name);
      this.close();

      if (!rates || !rates.has(key)) {
        // A remembered town can vanish when the courier's table is rebuilt.
        if (restoring) return this.forget();
        this.rows.hidden = true;
        this.miss.hidden = false;
        if (this.summary) this.summary.hidden = false;
        return;
      }

      // Always display the courier's spelling, whatever the customer typed or
      // what an older localStorage value holds.
      const label = rates.get(key).name;
      this.input.value = label;
      this.miss.hidden = true;
      if (this.summary) this.summary.hidden = true;
      this.set('[data-delivery-fee]', this.feeFor(key));
      if (this.baseEta) this.set('[data-delivery-eta]', this.etaFor(key));
      this.rows.hidden = false;
      if (this.heading && this.dataset.titleTemplate) {
        this.heading.textContent = this.dataset.titleTemplate.replace('[district]', label);
      }
      if (!restoring) {
        this.remember(key);
        this.announce(this.heading ? this.heading.textContent : '');
      }
    }

    close() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    announce(message) {
      if (this.status && message) this.status.textContent = message;
    }

    set(selector, value) {
      const el = this.querySelector(selector);
      if (el) el.textContent = value || '';
    }

    /* Storage can throw in a private window, and a remembered town is a
       convenience — never let it take the estimate down with it. */
    remember(value) {
      try {
        if (value) {
          window.localStorage.setItem(this.dataset.storageKey, value);
        } else {
          this.forget();
        }
      } catch (error) {
        /* no-op */
      }
    }

    recall() {
      try {
        return window.localStorage.getItem(this.dataset.storageKey);
      } catch (error) {
        return null;
      }
    }

    forget() {
      try {
        window.localStorage.removeItem(this.dataset.storageKey);
      } catch (error) {
        /* no-op */
      }
    }
  }

  if (!customElements.get('delivery-estimate')) {
    customElements.define('delivery-estimate', DeliveryEstimate);
  }

  /**
   * Product recommendations. `recommendations.products` is only populated when
   * the section is requested through the Recommendations API, so the section
   * renders empty and fetches itself. It removes itself outright if the fetch
   * fails or comes back with nothing, rather than leaving an empty heading.
   */
  class ProductRecommendations extends window.Theme.ThemeElement {
    init() {
      if (!this.dataset.url) return;

      // Only spend the request when the customer is on their way to it.
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '400px' }
      );
      this.observer.observe(this);
    }

    async load() {
      try {
        const doc = await window.Theme.fetchDocument(this.dataset.url);
        const incoming = doc.querySelector('product-recommendations');
        if (!incoming || !incoming.querySelector('.recommendations__inner')) {
          this.remove();
          return;
        }
        this.innerHTML = incoming.innerHTML;
      } catch (error) {
        this.remove();
      }
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  /**
   * The sticky buy bar, shown once the main Add to bag has scrolled away.
   *
   * Its button is outside the product form and reaches it with the `form`
   * attribute, so there is exactly one add-to-bag form on the page. Two forms
   * would be two places for the variant id to drift out of step.
   */
  class StickyBuyBar extends window.Theme.ThemeElement {
    init() {
      this.anchor = document.querySelector(this.dataset.watch || '');
      if (!this.anchor) return;

      // Deliberately NOT an IntersectionObserver. The anchor is a zero-height
      // sentinel, so an instant jump — "skip to content", a `#slide` link, the
      // browser restoring scroll on back — takes it from above the viewport to
      // below it without ever intersecting. Nothing crosses a threshold, no
      // callback fires, and the bar stays on screen over the buy button it was
      // standing in for. Measured: it did exactly that.
      //
      // One rect read per animation frame while scrolling has no failure mode
      // and costs nothing on a page that is already scrolling.
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.on(window, 'scroll', this.onScroll, { passive: true });
      this.on(window, 'resize', this.onScroll, { passive: true });
      this.update();
    }

    update() {
      // Show only once the buy area has genuinely gone past the top edge.
      this.hidden = this.anchor.getBoundingClientRect().top > 0;
    }

    destroy() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }
  }

  if (!customElements.get('sticky-buy-bar')) {
    customElements.define('sticky-buy-bar', StickyBuyBar);
  }

  /**
   * A small copy of the selected variant's photo, pinned to the top on mobile
   * while the customer is choosing Type and Age.
   *
   * Shown once the gallery has scrolled out of sight and until the variant
   * picker has gone past the top edge. Same rAF-throttled rect check as the
   * sticky buy bar, for the same reason: an instant jump crosses no threshold.
   */
  class VariantPeek extends window.Theme.ThemeElement {
    init() {
      const root = this.closest('.product') || document;
      this.gallery = root.querySelector('.product__media');
      this.picker = root.querySelector('variant-picker');
      if (!this.gallery || !this.picker) return;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.on(window, 'scroll', this.onScroll, { passive: true });
      this.on(window, 'resize', this.onScroll, { passive: true });
      this.on(this, 'click', () => {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.gallery.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      });
      this.update();
    }

    update() {
      // "Gone" means behind the sticky header, not just off the top of the
      // viewport: the header hides the bottom of the photo well before it
      // reaches 0, and the picker is usually already in view by then.
      const header = document.querySelector('[data-sticky="true"]');
      const edge = header ? header.getBoundingClientRect().bottom : 0;
      const galleryGone = this.gallery.getBoundingClientRect().bottom < edge + 8;
      const pickerAhead = this.picker.getBoundingClientRect().bottom > 0;
      // Sit directly under the sticky header rather than over it.
      this.style.insetBlockStart = `${Math.max(edge, 0)}px`;
      this.hidden = !(galleryGone && pickerAhead);
    }

    destroy() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }
  }

  if (!customElements.get('variant-peek')) {
    customElements.define('variant-peek', VariantPeek);
  }
})();
